import { supabase } from "./Supabase";
import { INPUT_LIMITS, clampFields, clampText } from "./lib/inputLimits";

const limitText = (value, key) =>
  clampText(value ?? "", INPUT_LIMITS[key] ?? 255);

const sanitizeInvoicePayload = (inv) => {
  const base = clampFields(
    {
      ...inv,
      company_name: inv.companyName,
      customer_name: inv.customerName,
      customer_address: inv.customerAddress,
      customer_city: inv.customerCity,
      customer_post_code: inv.customerPostCode,
      phone_number: inv.phoneNumber,
      pdf_path: inv.pdfPath,
      invoice_number: inv.invoiceNumber,
      invoice_date: inv.invoiceDate,
      supplied_date: inv.suppliedDate,
      post_code: inv.postCode,
      payee: inv.payee,
      sort_code: inv.sortCode,
      account_number: inv.accountNumber,
      notes: inv.notes,
      terms: inv.terms,
      address: inv.address,
      city: inv.city,
      email: inv.email,
    },
    {
      company_name: INPUT_LIMITS.companyName,
      address: INPUT_LIMITS.address,
      city: INPUT_LIMITS.city,
      post_code: INPUT_LIMITS.postCode,
      invoice_number: INPUT_LIMITS.invoiceNumber,
      customer_name: INPUT_LIMITS.customerName,
      customer_address: INPUT_LIMITS.customerAddress,
      customer_city: INPUT_LIMITS.customerCity,
      customer_post_code: INPUT_LIMITS.customerPostCode,
      phone_number: INPUT_LIMITS.phoneNumber,
      email: INPUT_LIMITS.email,
      payee: INPUT_LIMITS.payee,
      sort_code: INPUT_LIMITS.sortCode,
      account_number: INPUT_LIMITS.accountNumber,
      notes: INPUT_LIMITS.notes,
      terms: INPUT_LIMITS.terms,
    }
  );

  const items = Array.isArray(inv.items) ? inv.items : [];
  base.items = items.map((it) => ({
    ...it,
    description: limitText(it.description ?? "", "itemDescription"),
  }));

  return base;
};

export async function setInvoicePaid(id, isPaid) {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user) throw new Error("Not authenticated");

  const { error } = await supabase
    .from("invoices")
    .update({
      is_paid: isPaid,
      paid_at: isPaid ? new Date().toISOString() : null,
    })
    .eq("id", id)
    .eq("user_id", userData.user.id);

  if (error) throw error;
}

export async function saveInvoiceToDb(inv) {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user) throw new Error("Not authenticated");

  const sanitized = sanitizeInvoicePayload(inv);

  const { data, error } = await supabase
    .from("invoices")
    .insert({
      user_id: userData.user.id,
      company_name: sanitized.company_name,
      address: sanitized.address,
      city: sanitized.city,
      post_code: sanitized.post_code,
      invoice_number: sanitized.invoice_number,
      invoice_date: sanitized.invoice_date,
      supplied_date: sanitized.supplied_date,
      customer_name: sanitized.customer_name,
      customer_address: sanitized.customer_address ?? null,
      customer_city: sanitized.customer_city ?? null,
      customer_post_code: sanitized.customer_post_code ?? null,
      phone_number: sanitized.phone_number ?? null,
      email: sanitized.email ?? null,
      items: sanitized.items ?? [],
      subtotal: inv.subtotal,
      discount: inv.discount,
      deposit: inv.deposit,
      total: inv.total,
      payee: sanitized.payee ?? null,
      sort_code: sanitized.sort_code ?? null,
      account_number: sanitized.account_number ?? null,
      notes: sanitized.notes ?? null,
      terms: sanitized.terms ?? null,
      pdf_path: sanitized.pdf_path ?? null,
    })
    .select("id")
    .single();

  if (error) throw error;
  return Number(data.id);
}

export async function updateInvoiceInDb(inv) {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user) throw new Error("Not authenticated");

  const sanitized = sanitizeInvoicePayload(inv);

  const { error } = await supabase
    .from("invoices")
    .update({
      company_name: sanitized.company_name,
      address: sanitized.address,
      city: sanitized.city,
      post_code: sanitized.post_code,
      invoice_number: sanitized.invoice_number,
      invoice_date: sanitized.invoice_date,
      supplied_date: sanitized.supplied_date,
      customer_name: sanitized.customer_name,
      customer_address: sanitized.customer_address ?? null,
      customer_city: sanitized.customer_city ?? null,
      customer_post_code: sanitized.customer_post_code ?? null,
      phone_number: sanitized.phone_number ?? null,
      email: sanitized.email ?? null,
      items: sanitized.items ?? [],
      subtotal: inv.subtotal,
      discount: inv.discount,
      deposit: inv.deposit,
      total: inv.total,
      payee: sanitized.payee ?? null,
      sort_code: sanitized.sort_code ?? null,
      account_number: sanitized.account_number ?? null,
      notes: sanitized.notes ?? null,
      terms: sanitized.terms ?? null,
      pdf_path: sanitized.pdf_path ?? null,
    })
    .eq("id", inv.id)
    .eq("user_id", userData.user.id);

  if (error) throw error;
}

export async function getInvoicesForMetrics() {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user) throw new Error("Not authenticated");

  const { data, error } = await supabase
    .from("invoices")
    .select(
      `
      id,
      invoice_number,
      customer_name,
      items,
      subtotal,
      discount,
      deposit,
      total,
      is_paid,
      paid_at,
      pdf_path,
      created_at,
      invoice_date
    `
    )
    .eq("user_id", userData.user.id)
    .order("created_at", { ascending: false });

  if (error) throw error;

  return (data ?? []).map((r) => ({
    id: r.id,
    invoiceNumber: r.invoice_number,
    customerName: r.customer_name,

    items: typeof r.items === "string" ? JSON.parse(r.items) : r.items ?? [],

    subtotal: r.subtotal ?? 0,
    discount: r.discount ?? 0,
    deposit: r.deposit ?? 0,
    total: r.total ?? 0,
    isPaid: !!r.is_paid,
    paidAt: r.paid_at,
    pdfPath: r.pdf_path,
    createdAt: r.created_at,
    invoiceDate: r.invoice_date ?? undefined,
  }));
}

export async function getInvoices() {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user) throw new Error("Not authenticated");

  const { data, error } = await supabase
    .from("invoices")
    .select(
      `
      id,
      invoice_number,
      customer_name,
      items,
      subtotal,
      discount,
      deposit,
      total,
      is_paid,
      paid_at,
      pdf_path,
      created_at,
      invoice_date,
      customer_address,
      customer_city,
      customer_post_code
    `
    )
    .eq("user_id", userData.user.id)
    .order("created_at", { ascending: false });

  if (error) throw error;

  return (data ?? []).map((r) => ({
    id: r.id,
    invoiceNumber: r.invoice_number,
    customerName: r.customer_name,

    items: typeof r.items === "string" ? JSON.parse(r.items) : r.items ?? [],

    subtotal: r.subtotal ?? 0,
    discount: r.discount ?? 0,
    deposit: r.deposit ?? 0,
    total: r.total ?? 0,
    isPaid: !!r.is_paid,
    paidAt: r.paid_at ?? null,
    pdfPath: r.pdf_path,
    createdAt: r.created_at,
    invoiceDate: r.invoice_date ?? undefined,

    customerAddress: r.customer_address ?? null,
    customerCity: r.customer_city ?? null,
    customerPostCode: r.customer_post_code ?? null,
  }));
}

export async function getInvoiceById(id) {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user) throw new Error("Not authenticated");

  const { data, error } = await supabase
    .from("invoices")
    .select("*")
    .eq("id", id)
    .eq("user_id", userData.user.id)
    .single();

  if (error && error.code !== "PGRST116") throw error;
  if (!data) return null;

  return {
    id: data.id,
    companyName: data.company_name,
    address: data.address,
    city: data.city,
    postCode: data.post_code,
    invoiceNumber: data.invoice_number,
    invoiceDate: data.invoice_date,
    suppliedDate: data.supplied_date,
    customerName: data.customer_name,
    customerAddress: data.customer_address,
    customerCity: data.customer_city,
    customerPostCode: data.customer_post_code,
    phoneNumber: data.phone_number,
    email: data.email,
    items:
      typeof data.items === "string"
        ? JSON.parse(data.items)
        : data.items ?? [],
    subtotal: data.subtotal,
    discount: data.discount,
    deposit: data.deposit,
    total: data.total,
    payee: data.payee,
    sortCode: data.sort_code,
    accountNumber: data.account_number,
    notes: data.notes,
    terms: data.terms,
    pdfPath: data.pdf_path,
    createdAt: data.created_at,
  };
}

export async function deleteInvoice(id) {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user) throw new Error("Not authenticated");

  const { error } = await supabase
    .from("invoices")
    .delete()
    .eq("id", id)
    .eq("user_id", userData.user.id);

  if (error) throw error;
}
